import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { CdnConfig } from '@app/core/model/cdn-config.model'
import { ChangeRequest } from '@app/core/model/change-request.model'
import { ScopeVersion } from '@app/core/model/scope-version'
import { FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { ScopeActions } from '@app/features/scoping/store/actions/scoping.actions'
import { ScopeElementsService } from '@app/shared/components/scope-elements/services/scope-elements.service'
import { select, Store } from '@ngrx/store'
import { map, Observable } from 'rxjs'
import { DataMappingService } from '@app/features/scoping/service/data-mapping.service'
import { untilDestroyed } from '@app/shared/utils/utils'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { MappedEntity } from '@app/core/model/definitions/mapped-entity.interface'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { ChangeItem, ChangeType, FetchDeliverablePayload } from '../../model'
import { MenuOptions } from '@app/core/model/definitions/menu-options.interface'
import { Dictionary } from 'lodash'
import { ScopeUiModalComponent } from '@app/shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { MatDialog } from '@angular/material/dialog'
import { EditDeliverablePayload } from '../../model/edit-deliverable-payload.interface'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { ChangeRequestActions } from '../../store/actions/change-request.actions'

@Component({
  selector: 'change-request-tracked',
  templateUrl: './change-request-tracked.component.html',
})
export class ChangeRequestTrackedComponent implements OnChanges {
  @Input() currentScope: ScopeVersion
  @Input() currentChangeRequest: ChangeRequest
  @Input() changeItemsMap: Dictionary<ChangeItem>
  @Input() cdnConfig: CdnConfig
  @Input() sections: ScopeSection[]
  @Input() deliverables: Deliverable[]

  @Input() mainColumnClass: string
  @Input() componentMainColumnClass: string
  @Input() roleMainColumnClass: string

  @Output() onFetchDeliverable = new EventEmitter<FetchDeliverablePayload>()
  @Output() onEditDeliverable = new EventEmitter<EditDeliverablePayload>()
  @Output() onDeleteDeliverable = new EventEmitter<Deliverable>()
  @Output() onDeleteComponent = new EventEmitter<ScopeComponent>()

  scopeFees$: Observable<MappedEntity<FeeItemInstance>[]>

  selectedScopeId: number
  deliverableMenuOptions: MenuOptions[] = []
  componentMenuOptions: MenuOptions[] = []

  constructor(
    private store$: Store,
    private mappingService: DataMappingService,
    private dialog: MatDialog,
    protected scopeElementsService: ScopeElementsService
  ) {
    this.destroy$ = untilDestroyed()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentScope && this.currentScope && this.selectedScopeId != this.currentScope?.identity?.id) {
      this.initializeTableData()
    }

    this.selectedScopeId = this.currentScope?.identity?.id
  }

  fetchDeliverable(deliverableId: number) {
    this.onFetchDeliverable.emit({ scopeId: this.selectedScopeId, deliverableId })
  }

  private revertDeletedItem(item: Deliverable | ScopeComponent, callback: () => void) {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: {
        title: `Revert the deletion of "` + item.name + `"`,
        body: `Are you sure you want to revert the deletion of "` + item.name + `"?`,
        confirmText: 'Revert',
        confirmCallback: () => {
          dialog.close()
          callback()
        },
        cancelCallback: () => dialog.close(),
        inputs: [],
        flatButton: true,
        limitBodyWidth: true,
      },
    })
  }

  private initializeTableData() {
    this.store$.dispatch(ScopeActions.getAllFeeItems())

    this.setMenu()

    this.scopeFees$ = this.store$.pipe(
      select(ScopeOverviewSelectors.selectScopeFees),
      map((feeItems) =>
        this.mappingService.transformArray<FeeItemInstance>(feeItems, this.scopeElementsService.scopeFeeColumns)
      )
    )
  }

  private setMenu(): void {
    this.deliverableMenuOptions = [
      {
        isDetached: () => true,
        callback: (deliverable: Deliverable) =>
          this.onEditDeliverable.emit({
            scopeId: this.selectedScopeId,
            changeRequestId: this.currentChangeRequest.id,
            deliverableId: deliverable.id,
          }),
        name: () => 'Edit {LANG:deliverable}',
        icon: () => 'mode_edit',
      },
      {
        isHidden: (deliverable: Deliverable) => !this.isItemRemoved(deliverable),
        callback: (deliverable: Deliverable) =>
          this.revertDeletedItem(deliverable, () =>
            this.store$.dispatch(
              ChangeRequestActions.revertDeletedDeliverable({
                deliverableId: deliverable.changeAuditData?.identity || deliverable.id,
              })
            )
          ),
        name: () => `Revert`,
        icon: () => 'undo',
      },
      {
        isHidden: (deliverable: Deliverable) => this.isItemRemoved(deliverable),
        callback: (deliverable: Deliverable) => this.onDeleteDeliverable.emit(deliverable),
        name: () => `Delete`,
        icon: () => 'delete',
      },
    ]

    this.componentMenuOptions = [
      {
        isHidden: (component: ScopeComponent) => !this.isItemRemoved(component),
        callback: (component: ScopeComponent) =>
          this.revertDeletedItem(component, () =>
            this.store$.dispatch(
              ChangeRequestActions.revertDeletedComponent({
                componentId: component.changeAuditData?.identity || component.id,
              })
            )
          ),
        name: () => `Revert`,
        icon: () => 'undo',
      },
      {
        isHidden: (component: ScopeComponent) => this.isItemRemoved(component),
        callback: (element) => this.onDeleteComponent.emit(element),
        name: () => 'Delete',
        icon: () => 'delete',
      },
    ]
  }

  private isItemRemoved = (item: Deliverable | ScopeComponent): boolean =>
    this.changeItemsMap?.[item.changeAuditData?.identity || item.id]?.changeType === ChangeType.REMOVED

  protected readonly destroy$
}
